import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Row, Col, Input, FormFeedback } from "reactstrap";

import { ActivityButton } from "../../components/Common";
import { ErrorHandlingTypeEnum, GenderEnum } from "../../enums";
import { ParsedError, UpdateUserProfileRequest } from "../../interfaces";

import React from "react";
import {
  useLazyGetUserProfileQuery,
  useUpdateUserProfileMutation,
} from "../../store/api/accountApi";

export const PersonalDetails = () => {
  //#region hooks
  const { t } = useTranslation();
  //#endregion

  //#region internal state
  const [request] = useState<UpdateUserProfileRequest>();
  //#endregion

  const [
    getUserProfile,
    {
      data: dataUserProfile,
      isError: isErrorUserProfile,
      isLoading: isLoadingUserProfile,
    },
  ] = useLazyGetUserProfileQuery();

  const [
    updateUserProfile,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      isError: isErrorUpdate,
      error: errorUpdate,
    },
  ] = useUpdateUserProfileMutation();

  //#region form
  const form = useFormik<UpdateUserProfileRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || dataUserProfile?.name?.en || "",
        ar: request?.email || dataUserProfile?.name?.ar || "",
      },
      email: request?.email || dataUserProfile?.email?.email || "",
      gender: request?.gender || dataUserProfile?.gender || GenderEnum.MALE,
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      email: Yup.string().email(
        t("invalidEmail", {
          displayName: "email",
        }),
      ),
    }),
    onSubmit: (values) => {
      updateUserProfile(values);
    },
  });
  //#endregion

  //#region effects

  useEffect(() => {
    getUserProfile();
  }, []);

  useEffect(() => {
    if (!isLoadingUpdate && isSuccessUpdate) {
      toast.success(t("savedSuccessfully"), {
        autoClose: 3000,
      });
    }
  }, [isLoadingUpdate, isSuccessUpdate]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingUpdate]);

  //#endregion

  return (
    <React.Fragment>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <Row>
          <Col lg={6}>
            <div className="mb-3">
              <label htmlFor="name.en" className="form-label">
                {t("nameEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={isErrorUserProfile}
                autoComplete="off"
                id="name.en"
                name="name.en"
                value={form.values?.name.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.en && form.errors.name?.en ? true : false
                }
              />
              {form.touched.name?.en && form.errors.name?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.en as string}
                </FormFeedback>
              ) : null}
            </div>
          </Col>
          <Col lg={6}>
            <div className="mb-3">
              <label htmlFor="name.ar" className="form-label">
                {t("nameAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={isErrorUserProfile}
                autoComplete="off"
                id="name.ar"
                name="name.ar"
                value={form.values?.name.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.ar && form.errors.name?.ar ? true : false
                }
              />
              {form.touched.name?.ar && form.errors.name?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
          </Col>
          <Col lg={6}>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                {t("email")}
              </label>
              <Input
                disabled={isErrorUserProfile}
                autoComplete="off"
                id="email"
                name="email"
                value={form.values?.email}
                type="text"
                className="form-control"
                placeholder={t("enterEmail")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={form.touched.email && form.errors.email ? true : false}
              />
              {form.touched.email && form.errors.email ? (
                <FormFeedback type="invalid">
                  {form.errors.email as string}
                </FormFeedback>
              ) : null}
            </div>
          </Col>
          <Col lg={6} className="mt-1">
            <label className="form-label">{t("gender")}</label>
            <div>
              <div className="form-check form-check-inline">
                <Input
                  className="form-check-input"
                  type="radio"
                  name="inlineRadioOptions"
                  id="inlineRadio1"
                  value={GenderEnum.MALE}
                  checked={form.values.gender === GenderEnum.MALE}
                  onChange={() => {
                    form.setFieldValue("gender", GenderEnum.MALE);
                  }}
                />
                <label className="form-check-label" htmlFor="inlineRadio1">
                  {t("male")}
                </label>
              </div>
              <div className="form-check form-check-inline">
                <Input
                  className="form-check-input"
                  type="radio"
                  name="inlineRadioOptions"
                  id="inlineRadio2"
                  value={GenderEnum.FEMALE}
                  checked={form.values.gender === GenderEnum.FEMALE}
                  onChange={() => {
                    form.setFieldValue("gender", GenderEnum.FEMALE);
                  }}
                />
                <label className="form-check-label" htmlFor="inlineRadio2">
                  {t("female")}
                </label>
              </div>
            </div>
          </Col>
          <Col lg={12}>
            <div className="hstack gap-2 justify-content-end">
              <ActivityButton
                title="save"
                inProgressTitle="saving"
                color="success"
                className="btn btn-success"
                type="submit"
                inProgress={isLoadingUpdate}
                disabled={isLoadingUpdate || isLoadingUserProfile}
              ></ActivityButton>
            </div>
          </Col>
        </Row>
      </Form>
    </React.Fragment>
  );
};
